import {
  ProspectionServicePriceMode,
  ProspectionServiceResource,
} from "@/interfaces";

/**
 * Whether a single pricing option can be sold.
 *
 * A price of zero means the company has not priced this cell for the customer's address, and the
 * backend refuses to attach it. The one legitimate zero is the "no charge until the included
 * visits run out" model, which is recognisable because the cell carries an overage rate.
 *
 * This mirrors `AttachProspectionServiceAction` exactly — the two have to agree, or the portal
 * either offers something the server will refuse or hides something it would have accepted.
 */
export const isOfferablePriceMode = (
  mode: ProspectionServicePriceMode
): boolean => Number(mode.amount) > 0 || (mode.overage_rate ?? null) !== null;

/** The pricing options of a service that the customer may actually pick. */
export const offerablePriceModes = (
  service: ProspectionServiceResource
): ProspectionServicePriceMode[] =>
  (service.attributes.price_modes ?? []).filter(isOfferablePriceMode);

/**
 * Whether a service has anything sellable left. A service with no catalog modes is priced flat,
 * so its own `price_total` answers for it; otherwise it is offerable while at least one of its
 * modes is — filtering at service level alone would hide a service whose seasonal mode is zero
 * but whose per-visit mode is priced.
 */
export const isOfferableProspectionService = (
  service: ProspectionServiceResource
): boolean =>
  (service.attributes.price_modes ?? []).length > 0
    ? offerablePriceModes(service).length > 0
    : Number(service.attributes.price_total) > 0;

export const offerableProspectionServices = (
  services: ProspectionServiceResource[] | undefined
): ProspectionServiceResource[] =>
  (services ?? []).filter(isOfferableProspectionService);
